import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { intakeAPI } from '../utils/api';
import axios from 'axios';
import {
  BUSINESS_REGISTRATION_AUTHORITIES,
  BUSINESS_TYPES,
  SUPPORTED_COUNTRIES,
  DEFAULT_BUSINESS_TYPE
} from '../constants/businessFields';
import {
  REQUIRED_FIELDS,
  getFieldValue,
  getFieldStatus,
  errorKeyFor,
  getFieldError,
  getMissingRequiredFields,
  getRequiredProgress
} from '../utils/formStatus';
import { useFormDraft } from '../hooks/useFormDraft';
import FieldStatusIcon from '../components/FieldStatusIcon';
import Tooltip from '../components/Tooltip';

// Explanation shown in the "Why is this required?" tooltip on Business Website.
const BUSINESS_WEBSITE_HELP =
  'A website for the Church or Organization is required and must contain the phone number and address of the Organization. ' +
  'If there is not a current website, social media links are acceptable (Facebook, Instagram, Twitter). ' +
  'Social media accounts must be set to public and must be a group or page, not a personal profile.';

// Default form configurations for each submission type (fallbacks if API fails)
const DEFAULT_FORM_CONFIG = {
  text_giving: {
    displayName: 'Text Giving',
    intakeFormTitle: 'Text Giving Verification Request',
    intakeFormSubtitle: 'Please complete the missing fields to request verification for your Text Giving phone number',
    phoneFieldLabel: 'Text Giving Phone Number',
    phoneFieldDescription: 'Enter the Tithely assigned Text Giving Number that donors will use for text to give.',
    phoneHelpTooltip: 'The Text Giving number can be found within your Text Giving settings of your account. If you are not able to locate this, please contact our Support team using the Help Widget in the lower right hand corner of your account when you are logged in.'
  },
  messaging: {
    displayName: 'Messaging',
    intakeFormTitle: 'Messaging Verification Request',
    intakeFormSubtitle: 'Please complete the missing fields to request verification for your Messaging phone number',
    phoneFieldLabel: 'Messaging Phone Number',
    phoneFieldDescription: 'Enter the Tithely assigned Messaging Number that will be used for church communications.',
    phoneHelpTooltip: 'The Messaging number can be found within your Messaging settings of your account. If you are not able to locate this, please contact our Support team using the Help Widget in the lower right hand corner of your account when you are logged in.'
  }
};

// Maps URL query params -> the dotted field keys used by formStatus / the form.
const URL_PARAM_TO_KEY = {
  org_name: 'organization_name',
  phone_number: 'phone_number',
  contact_email: 'contact_email',
  contact_name: 'contact_name',
  contact_phone: 'contact_phone',
  website: 'business_details.business_website',
  dba: 'business_details.doing_business_as',
  ein: 'business_details.business_registration_number',
  ein_authority: 'business_details.business_registration_authority',
  business_type: 'business_details.business_type',
  ein_country: 'business_details.business_registration_country',
  street: 'address.street',
  city: 'address.city',
  state: 'address.state',
  zip: 'address.zip',
  country: 'address.country'
};

const ADDRESS_PREFIX = 'address.';
const BD_PREFIX = 'business_details.';

// Write a dotted field key into a nested formData object (mirrors getFieldValue).
function setFieldValue(obj, key, value) {
  if (key.startsWith(ADDRESS_PREFIX)) {
    obj.business_details.address[key.slice(ADDRESS_PREFIX.length)] = value;
  } else if (key.startsWith(BD_PREFIX)) {
    obj.business_details[key.slice(BD_PREFIX.length)] = value;
  } else {
    obj[key] = value;
  }
}

// Deep-merge a saved draft snapshot onto a base formData object.
function mergeDraft(base, draft) {
  if (!draft) return base;
  return {
    ...base,
    ...draft,
    business_details: {
      ...base.business_details,
      ...(draft.business_details || {}),
      address: {
        ...base.business_details.address,
        ...((draft.business_details && draft.business_details.address) || {})
      }
    }
  };
}

function createDefaultFormData() {
  return {
    organization_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    phone_number: '',
    phone_number_type: 'toll-free', // Default to toll-free
    zendesk_org_id: '', // Store the Hub OID
    business_details: {
      business_type: DEFAULT_BUSINESS_TYPE, // Default for all Tithely customers (NON_PROFIT)
      business_website: '',
      doing_business_as: '', // DBA name (optional)
      business_registration_number: '', // EIN or other registration number
      business_registration_authority: '', // e.g., 'EIN' for US
      business_registration_country: 'US', // Required if registration number is provided
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US'
      }
    }
  };
}

export default function IntakeForm({ submissionType: propSubmissionType }) {
  const location = useLocation();

  // Determine submission type from prop, route, or default to 'text_giving'
  const getSubmissionType = () => {
    if (propSubmissionType) return propSubmissionType;
    if (location.pathname.includes('/messaging')) return 'messaging';
    return 'text_giving';
  };

  const submissionType = getSubmissionType();

  const [formConfig, setFormConfig] = useState(DEFAULT_FORM_CONFIG[submissionType]);
  const [formData, setFormData] = useState(createDefaultFormData);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [phoneNumberLocked, setPhoneNumberLocked] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [configLoadError, setConfigLoadError] = useState(false);

  // Which fields arrived already filled (via URL params or the prefill API).
  const [prefilledFields, setPrefilledFields] = useState(() => new Set());
  // Whether the customer has attempted a submit (gates the amber "missing" cue
  // so a fresh blank form isn't a wall of highlights).
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Whether a saved draft was restored on mount (drives the restore banner).
  const [draftRestored, setDraftRestored] = useState(false);
  // True once the customer edits anything — only then do we persist a draft, so
  // merely receiving a prefilled link never creates a "restore" prompt later.
  const userEditedRef = useRef(false);

  // Browser autosave / resume.
  const shouldPersist = useCallback(() => userEditedRef.current, []);
  const { restoredDraft, clearDraft } = useFormDraft(formData, {
    storageKey: `intakeForm:draft:${submissionType}`,
    shouldPersist
  });

  // Load form configuration from API
  useEffect(() => {
    const loadFormConfig = async () => {
      setConfigLoading(true);
      setConfigLoadError(false);
      try {
        const response = await axios.get(`/api/submission-configs/${submissionType}/form`);
        if (response.data) {
          setFormConfig(response.data);
        }
      } catch (err) {
        console.error('Failed to load form config, using defaults:', err);
        setConfigLoadError(true);
        // Keep using default config
      } finally {
        setConfigLoading(false);
      }
    };

    loadFormConfig();
  }, [submissionType]);

  // Restore draft + apply URL params / API prefill on mount.
  // Order: defaults -> saved draft -> URL params (fill only-empty; phone always
  // sets + locks) -> API prefill (fills still-empty). This keeps the customer's
  // own saved edits from being clobbered by static prefill.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsEmbedded(params.get('embedded') === 'true');

    const urlOid = params.get('oid');
    const provided = {};
    Object.entries(URL_PARAM_TO_KEY).forEach(([param, key]) => {
      const raw = params.get(param);
      if (raw != null && raw !== '') provided[key] = decodeURIComponent(raw);
    });

    const pf = new Set();
    const hasUrlData = Object.keys(provided).length > 0 || !!urlOid;

    if (restoredDraft || hasUrlData) {
      setFormData(prev => {
        const base = mergeDraft(JSON.parse(JSON.stringify(prev)), restoredDraft);
        if (urlOid && !base.zendesk_org_id) base.zendesk_org_id = urlOid;
        Object.entries(provided).forEach(([key, value]) => {
          const current = getFieldValue(base, key);
          if (key === 'phone_number' || !current || !String(current).trim()) {
            setFieldValue(base, key, value);
            pf.add(key);
          }
        });
        return base;
      });
    }

    if (restoredDraft) setDraftRestored(true);
    if (provided.phone_number) setPhoneNumberLocked(true);
    if (pf.size) setPrefilledFields(pf);

    // Phase 2: if OID provided, fetch full data from API (fills remaining gaps)
    if (urlOid) {
      loadPrefillData(urlOid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPrefillData = async (oid) => {
    setPrefilling(true);
    try {
      const response = await axios.get(`/api/prefill/${oid}`);
      const data = response.data;

      const apiValues = {
        organization_name: data.organization_name,
        phone_number: data.phone_number,
        'business_details.business_website': data.business_details?.business_website,
        'business_details.business_type': data.business_details?.business_type,
        'business_details.doing_business_as': data.business_details?.doing_business_as,
        'business_details.business_registration_number': data.business_details?.business_registration_number,
        'business_details.business_registration_authority': data.business_details?.business_registration_authority,
        'business_details.business_registration_country': data.business_details?.business_registration_country,
        'address.street': data.business_details?.address?.street,
        'address.city': data.business_details?.address?.city,
        'address.state': data.business_details?.address?.state,
        'address.zip': data.business_details?.address?.zip,
        'address.country': data.business_details?.address?.country
      };

      const addedKeys = [];
      setFormData(prev => {
        const base = JSON.parse(JSON.stringify(prev));
        if (data.zendesk_org_id && !base.zendesk_org_id) base.zendesk_org_id = data.zendesk_org_id;
        Object.entries(apiValues).forEach(([key, value]) => {
          if (value == null || value === '') return;
          const current = getFieldValue(base, key);
          if (!current || !String(current).trim()) {
            setFieldValue(base, key, value);
            addedKeys.push(key);
          }
        });
        return base;
      });

      if (addedKeys.length) {
        setPrefilledFields(prev => {
          const next = new Set(prev);
          addedKeys.forEach(k => next.add(k));
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to load prefill data:', err);
      // Non-fatal - user can still fill out form manually
    } finally {
      setPrefilling(false);
    }
  };

  // Check for duplicate phone numbers
  const checkForDuplicates = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setDuplicateCheck(null);
      return;
    }

    setCheckingDuplicate(true);
    try {
      const response = await intakeAPI.checkPhone(phoneNumber);
      setDuplicateCheck(response.data);
    } catch (err) {
      console.error('Failed to check for duplicates:', err);
      // Don't block user if check fails
      setDuplicateCheck(null);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Debounced phone number check
  useEffect(() => {
    // Skip duplicate check if phone number is locked (pre-filled from URL)
    if (phoneNumberLocked) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (formData.phone_number) {
        checkForDuplicates(formData.phone_number);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.phone_number, phoneNumberLocked]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    userEditedRef.current = true;

    // Editing a prefilled field makes it the customer's own — drop the marker
    // so its de-emphasized styling lifts and it reads as "completed by you".
    setPrefilledFields(prev => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });

    // Clear field-level error when user modifies a field
    if (name.startsWith('business_details.')) {
      const field = name.replace('business_details.', '');
      if (fieldErrors[field]) {
        setFieldErrors(prev => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
      setFormData(prev => ({
        ...prev,
        business_details: {
          ...prev.business_details,
          [field]: value
        }
      }));
    } else if (name.startsWith('address.')) {
      const field = name.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        business_details: {
          ...prev.business_details,
          address: {
            ...prev.business_details.address,
            [field]: value
          }
        }
      }));
    } else {
      // Clear field-level error for top-level fields (e.g. contact_phone)
      if (fieldErrors[name]) {
        setFieldErrors(prev => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitAttempted(true);
    setSubmitting(true);

    // Validate every required field (drives the red error state + scroll-to-first)
    const newFieldErrors = {};
    REQUIRED_FIELDS.forEach(({ key, label }) => {
      const message = getFieldError(formData, key, label);
      if (message) {
        newFieldErrors[errorKeyFor(key)] = message;
      }
    });

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      const missing = getMissingRequiredFields(formData).map(f => f.label);
      setError(`Please complete the following: ${missing.join(', ')}`);
      setSubmitting(false);
      // Scroll to the first field with an error
      const firstErrorField = document.querySelector('[data-field-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check for duplicates if not already blocked
    if (duplicateCheck && !duplicateCheck.canProceed) {
      setError(duplicateCheck.statusMessage || 'This phone number already has a pending verification request.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await intakeAPI.submit({ ...formData, submission_type: submissionType });
      setSubmissionId(response.data.submission.id);
      setTicketId(response.data.submission.ticket_id);
      setSubmitted(true);
      // Progress is complete — discard the saved draft.
      userEditedRef.current = false;
      clearDraft();

      // Notify parent window if embedded
      if (isEmbedded && window.parent) {
        window.parent.postMessage({
          type: 'form-submitted',
          submissionId: response.data.submission.id,
          ticketId: response.data.submission.ticket_id
        }, '*');
      }
    } catch (err) {
      // Handle duplicate submission error from backend
      if (err.response?.status === 409) {
        const existing = err.response.data.existingSubmission;
        setError(
          `A verification request is already in progress for this phone number. ` +
          `Ticket #${existing?.ticket_id || 'unknown'} was created on ${existing?.created_at ? new Date(existing.created_at).toLocaleDateString() : 'unknown date'}.`
        );
      } else {
        setError(err.response?.data?.error || 'Failed to submit form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close button in embedded mode
  const handleClose = () => {
    if (isEmbedded && window.parent) {
      window.parent.postMessage({ type: 'close-form' }, '*');
    }
  };

  // Discard a restored draft and reload the pristine (prefilled) form.
  const handleStartOver = () => {
    userEditedRef.current = false;
    clearDraft();
    try {
      window.location.reload();
    } catch {
      // Fallback: clear in-memory state if reload is unavailable
      setFormData(createDefaultFormData());
      setPrefilledFields(new Set());
      setDraftRestored(false);
      setSubmitAttempted(false);
      setFieldErrors({});
    }
  };

  // ---- Derived per-field status helpers (used throughout render) ----
  const showMissing = prefilledFields.size > 0 || submitAttempted;

  const statusFor = (key, required = true) => {
    const status = getFieldStatus(formData, key, { prefilledFields, fieldErrors, required });
    if (status === 'missing' && !showMissing) return 'neutral';
    return status;
  };

  const stateClasses = (status, iconPad) => {
    const pad = iconPad ? ' pr-10' : '';
    switch (status) {
      case 'error':
        return 'border-red-500 focus:ring-red-500';
      case 'missing':
        return `border-amber-300 border-l-4 border-l-amber-400 bg-amber-50/50${pad}`;
      case 'complete':
        return `border-green-300${pad}`;
      case 'prefilled':
        return `bg-gray-50 border-gray-200 text-gray-600${pad}`;
      default:
        return '';
    }
  };

  const inputClasses = (key, required = true) => `input ${stateClasses(statusFor(key, required), true)}`.trim();
  const selectClasses = (key, required = true) => `input ${stateClasses(statusFor(key, required), false)}`.trim();

  // Render a standard text-style field (text/email/tel) with status styling +
  // the overlay green-check / amber cue. Returns elements (not a component) so
  // inputs keep their identity across renders and never lose focus.
  const renderTextField = ({ label, name, type = 'text', placeholder, required = true, autoComplete, help, labelRight }) => {
    const errKey = errorKeyFor(name);
    return (
      <div data-field-error={!!fieldErrors[errKey] || undefined}>
        {labelRight ? (
          <div className="mb-1 flex items-center justify-between gap-2">
            <label className="block text-sm font-medium text-secondary-900">{label}{required ? ' *' : ''}</label>
            {labelRight}
          </div>
        ) : (
          <label className="label">{label}{required ? ' *' : ''}</label>
        )}
        <div className="relative">
          <input
            type={type}
            name={name}
            value={getFieldValue(formData, name)}
            onChange={handleChange}
            required={required}
            autoComplete={autoComplete}
            className={inputClasses(name, required)}
            placeholder={placeholder}
          />
          <FieldStatusIcon
            status={statusFor(name, required)}
            className="absolute inset-y-0 right-3 my-auto pointer-events-none"
          />
        </div>
        {fieldErrors[errKey] && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors[errKey]}</p>
        )}
        {help}
      </div>
    );
  };

  const progress = getRequiredProgress(formData);
  const missingFields = getMissingRequiredFields(formData);
  const progressPct = Math.round((progress.complete / progress.total) * 100);
  const allComplete = missingFields.length === 0;
  // The consolidated progress card appears once there's something to summarize:
  // after a prefill, after a submit attempt, or when a saved draft was restored
  // (the restored case is what hosts the "Start over" affordance).
  const showProgressCard = prefilledFields.size > 0 || submitAttempted || draftRestored;
  // The green-check explainer only makes sense when fields actually arrived
  // pre-completed — via prefill/URL autofill or a restored draft.
  const showGreenCheckHint = prefilledFields.size > 0 || draftRestored;
  // "Where can I find this?" help copy — from the API form config, falling back
  // to the built-in per-type default.
  const phoneHelpTooltip = formConfig.phoneHelpTooltip || DEFAULT_FORM_CONFIG[submissionType]?.phoneHelpTooltip;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="card text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission Received!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for submitting your verification request. Our team will review it shortly.
            </p>
            {ticketId && (
              <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-primary-700 mb-1">Your Ticket Number:</p>
                <p className="text-3xl font-bold text-primary-900">{ticketId}</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Reference ID:</p>
              <p className="text-sm font-mono text-gray-700">{submissionId}</p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              You'll receive an email confirmation shortly. Please save your ticket number for future reference.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              {isEmbedded ? (
                <button
                  onClick={handleClose}
                  className="btn btn-primary w-full"
                >
                  Close
                </button>
              ) : (
                <>
                  <Link to="/status" className="btn btn-primary flex-1">
                    Check Status
                  </Link>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData(createDefaultFormData());
                      setPrefilledFields(new Set());
                      setDraftRestored(false);
                      setSubmitAttempted(false);
                      setFieldErrors({});
                      setPhoneNumberLocked(false);
                      setDuplicateCheck(null);
                      userEditedRef.current = false;
                      clearDraft();
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Submit Another Request
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {!isEmbedded && (
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {formConfig.intakeFormTitle || 'Phone Number Verification Request'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {formConfig.intakeFormSubtitle || 'Please fill out this form to request verification for your phone number'}
            </p>
          </div>
        )}

        {isEmbedded && (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {formConfig.intakeFormTitle || 'Verification Request'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
              aria-label="Close"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card" noValidate>
          {configLoadError && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4">
              <p className="font-medium flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Unable to load custom form configuration. Using default settings.
              </p>
            </div>
          )}

          {prefilling && (
            <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4">
              <p className="font-medium flex items-center">
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading your organization's information...
              </p>
            </div>
          )}

          {showProgressCard && (
            <div className={`mb-6 rounded-lg border p-4 ${allComplete ? 'border-green-200 bg-green-50' : 'border-primary-100 bg-primary-50'}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {allComplete
                      ? 'All set — every required field is complete.'
                      : `${progress.complete} of ${progress.total} required fields complete`}
                  </p>
                  {draftRestored && (
                    <button
                      type="button"
                      onClick={handleStartOver}
                      className="text-sm font-medium text-primary-700 hover:text-primary-900 underline flex-shrink-0"
                    >
                      Start over
                    </button>
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-500 tabular-nums flex-shrink-0">{progressPct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${allComplete ? 'bg-green-500' : 'bg-primary-500'}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {!allComplete && showGreenCheckHint && (
                <p className="mt-3 text-xs text-gray-600">
                  Fields marked with a <span className="font-medium text-gray-700">green check</span> were previously completed
                  or autofilled based on what we know. Please review those and complete the highlighted fields below.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Organization Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Organization Information</h2>
            <div className="grid grid-cols-1 gap-4">
              {renderTextField({
                label: 'Organization Name',
                name: 'organization_name',
                placeholder: 'Your Church Name'
              })}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {renderTextField({
                  label: 'Contact Name',
                  name: 'contact_name',
                  placeholder: 'John Doe'
                })}
                {renderTextField({
                  label: 'Contact Email',
                  name: 'contact_email',
                  type: 'email',
                  placeholder: 'john@example.com'
                })}
              </div>

              {renderTextField({
                label: 'Contact Phone',
                name: 'contact_phone',
                type: 'tel',
                placeholder: '+1 (555) 123-4567'
              })}
            </div>
          </div>

          {/* Phone Number Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {formConfig.phoneFieldLabel || 'Phone Number'}
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <div data-field-error={!!fieldErrors.phone_number || undefined}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-secondary-900">{formConfig.phoneFieldLabel || 'Phone Number'} *</label>
                  {!phoneNumberLocked && phoneHelpTooltip && (
                    <Tooltip label="Where can I find this?">{phoneHelpTooltip}</Tooltip>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {formConfig.phoneFieldDescription || 'Enter the phone number to verify.'}
                </p>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    readOnly={phoneNumberLocked}
                    className={`input ${phoneNumberLocked ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''} ${
                      (duplicateCheck && !duplicateCheck.canProceed) || fieldErrors.phone_number ? 'border-red-500 focus:ring-red-500' : ''
                    } ${
                      statusFor('phone_number') === 'missing' && !phoneNumberLocked && !(duplicateCheck && !duplicateCheck.canProceed) && !fieldErrors.phone_number
                        ? 'border-amber-300 border-l-4 border-l-amber-400 bg-amber-50/50'
                        : ''
                    }`}
                    placeholder="+1 (800) 555-1234"
                  />
                  {phoneNumberLocked && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  )}
                  {checkingDuplicate && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                {phoneNumberLocked && (
                  <p className="mt-1 text-sm text-gray-500">
                    This {formConfig.displayName || ''} number was assigned automatically by Tithely and cannot be changed.
                  </p>
                )}

                {fieldErrors.phone_number && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.phone_number}</p>
                )}

                {/* Duplicate Warning */}
                {duplicateCheck && !duplicateCheck.canProceed && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-red-800">{duplicateCheck.statusMessage}</p>
                        {duplicateCheck.submission && (
                          <p className="text-xs text-red-700 mt-1">
                            Existing request: Ticket #{duplicateCheck.submission.ticketId || 'N/A'}
                            {duplicateCheck.submission.organizationName && ` for ${duplicateCheck.submission.organizationName}`}
                          </p>
                        )}
                        {duplicateCheck.twilioVerification && !duplicateCheck.submission && (
                          <p className="text-xs text-red-700 mt-1">
                            Verification Status: {duplicateCheck.twilioVerification.friendlyStatus || duplicateCheck.twilioVerification.status}
                            {duplicateCheck.twilioVerification.businessName && ` for ${duplicateCheck.twilioVerification.businessName}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Business Details</h2>
            <div className="grid grid-cols-1 gap-4">
              {renderTextField({
                label: 'Business Website',
                name: 'business_details.business_website',
                placeholder: 'www.example.com or https://facebook.com/yourchurch',
                labelRight: (
                  <Tooltip label="Why is this required?">{BUSINESS_WEBSITE_HELP}</Tooltip>
                )
              })}

              {renderTextField({
                label: 'Doing Business As (DBA)',
                name: 'business_details.doing_business_as',
                required: false,
                placeholder: 'Alternate business name (if applicable)',
                help: (
                  <p className="mt-1 text-xs text-gray-500">
                    Only fill this in if the organization operates under a different name than the legal name.
                  </p>
                )
              })}

              {/* Business Registration Number Section */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Business Registration</h3>
                <p className="text-xs text-gray-600 mb-4">
                  Your EIN (Employer Identification Number) is required for text messaging verification.
                  This information helps verify your organization's identity.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {renderTextField({
                    label: 'EIN Registration Number',
                    name: 'business_details.business_registration_number',
                    placeholder: '12-3456789'
                  })}
                  <div data-field-error={!!fieldErrors.business_registration_authority || undefined}>
                    <label className="label flex items-center justify-between">
                      <span>Registration Authority *</span>
                      <FieldStatusIcon status={statusFor('business_details.business_registration_authority')} />
                    </label>
                    <select
                      name="business_details.business_registration_authority"
                      value={formData.business_details.business_registration_authority}
                      onChange={handleChange}
                      className={selectClasses('business_details.business_registration_authority')}
                      required
                    >
                      <option value="">Select one...</option>
                      {BUSINESS_REGISTRATION_AUTHORITIES.map((authority) => (
                        <option key={authority.value} value={authority.value}>
                          {authority.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.business_registration_authority && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.business_registration_authority}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                  <div data-field-error={!!fieldErrors.business_type || undefined}>
                    <label className="label flex items-center justify-between">
                      <span>Business Type *</span>
                      <FieldStatusIcon status={statusFor('business_details.business_type')} />
                    </label>
                    <select
                      name="business_details.business_type"
                      value={formData.business_details.business_type}
                      onChange={handleChange}
                      className={selectClasses('business_details.business_type')}
                      required
                    >
                      <option value="">Select business type...</option>
                      {BUSINESS_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.business_type && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.business_type}</p>
                    )}
                  </div>
                  <div data-field-error={!!fieldErrors.business_registration_country || undefined}>
                    <label className="label flex items-center justify-between">
                      <span>Registration Country *</span>
                      <FieldStatusIcon status={statusFor('business_details.business_registration_country')} />
                    </label>
                    <select
                      name="business_details.business_registration_country"
                      value={formData.business_details.business_registration_country}
                      onChange={handleChange}
                      className={selectClasses('business_details.business_registration_country')}
                      required
                    >
                      {SUPPORTED_COUNTRIES.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.business_registration_country && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.business_registration_country}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Note: Business type, use case, messaging volume, and opt-in process fields removed */}
              {/* These now use static values defined in backend/services/twilioService.js */}
              {/* All Text-to-Give campaigns use the same standardized values */}

              {/* Business address flows within Business Details — no separate section header */}
              {renderTextField({
                label: 'Business Address',
                name: 'address.street',
                placeholder: '123 Main St'
              })}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {renderTextField({
                  label: 'City',
                  name: 'address.city',
                  placeholder: 'City'
                })}
                {renderTextField({
                  label: 'State',
                  name: 'address.state',
                  placeholder: 'ST'
                })}
                <div className="col-span-2 sm:col-span-1">
                  {renderTextField({
                    label: 'Zip Code',
                    name: 'address.zip',
                    placeholder: '12345'
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={configLoading || submitting || (duplicateCheck && !duplicateCheck.canProceed)}
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
